/**
 * @module employee.transformer
 */

/**
 * Map a single employee returned by server to usable format by front-end UI.
 *
 * @example rawEmployee (from server)
 * {
 *   "id": 123,
 *   "enabled": true,
 *   "languageId": 1,
 *   "employeeId": "rob.mclarty@worktango.com",
 *   "firstName": "Rob",
 *   "lastName": "McLarty",
 *   "email": "rob.mclarty@worktango.com",
 *   "attributes": [
 *     { "id": 123, "value": "My Value" },
 *     { "id": 456, "value": "My Other Value" },
 *   ]
 * }
 *
 * @example output (employee)
 * {
 *   id: 123,
 *   lang: 'en-ca',
 *   companyEmployeeId: 'rob.mclarty@worktango.com',
 *   isActive: true,
 *   email: 'rob.mclarty@worktango.com',
 *   name: 'Rob McLarty',
 *   firstName: 'Rob',
 *   lastName: 'McLarty',
 *   employeeAttributes: [123, 456],
 *   employeeAttributeValues: {
 *     123: "My Value",
 *     456: "My Other Value"
 *   }
 * }
 */
const employeeFrom = (rawEmployee) => {
  const name =
    rawEmployee.firstName || rawEmployee.lastName
      ? `${rawEmployee.firstName} ${rawEmployee.lastName}`
      : rawEmployee.email?.substring(0, rawEmployee.email?.lastIndexOf('@')) || rawEmployee.employeeId;

  // WARNING: `lang` should be appended to `rawEmployee` prior to calling this
  // function otherwise we have no way of mapping the database ID to the
  // corresponding ISO code. There is a set of `languages` entities in the Redux
  // store which has a mapping of database ID => ISO code and is loaded on init ;)
  const lang = rawEmployee.lang ? rawEmployee.lang : null;

  const employeeAttributeValues = rawEmployee.attributes?.reduce(
    (dict, attr) => ({
      ...dict,
      [attr.id]: attr.value,
    }),
    {},
  );

  const employee = {
    id: Number(rawEmployee.id),
    companyEmployeeId: rawEmployee.employeeId, // Is `companyEmployeeId` distinguishable enough from other database IDs?
    lang,
    isActive: Boolean(rawEmployee.enabled),
    email: rawEmployee.email,
    name,
    firstName: rawEmployee.firstName,
    lastName: rawEmployee.lastName,
    employeeAttributes: rawEmployee.attributes,
    employeeAttributeValues,
    isUser: rawEmployee.isUser,
    isPrimaryEmployee: rawEmployee.isPrimaryEmployee,
  };

  // Remove any "undefined" properties.
  const cleanEmployee = Object.keys(employee).reduce((acc, key) => {
    if (employee[key] === undefined) return acc;

    return {
      ...acc,
      [key]: employee[key],
    };
  }, {});

  return cleanEmployee;
};

// Transform a list of employees.
const employeesFrom = (rawEmployees = []) => {
  const employees = rawEmployees.map((rawEmployee) => employeeFrom(rawEmployee));

  return employees;
};

/**
 * Transform updated/created employee (uses a different format than fetch).
 *
 * @example updatedEmployee (from server updateEmployee or createEmployee)
 * {
 *   "id": 123,
 *   "enabled": true,
 *   "languageId": 1,
 *   "attributesWithValues": [
 *     {
 *       "attribute": {
 *         "id": 50,
 *         "attributeNames": [
 *           {
 *             "id": 44,
 *             "name": "First Name",
 *             "language": {
 *               "id": 1,
 *               "languageISOCode": "en-ca"
 *             }
 *           }
 *         ]
 *       },
 *       "attributeValues": [
 *         "Rob"
 *       ]
 *     },
 *     {
 *       "attribute": {
 *         "id": 51,
 *         "attributeNames": [
 *           {
 *             "id": 45,
 *             "name": "Last Name",
 *             "language": {
 *               "id": 1,
 *               "languageISOCode": "en-ca"
 *             }
 *           }
 *         ]
 *       },
 *       "attributeValues": [
 *         "McLarty"
 *       ]
 *     }
 *   ]
 * }
 */
const updatedEmployeeFrom = (rawUpdatedEmployee = {}) => {
  const employeeAttributes = [];
  const employeeAttributeValues = {};
  const coreProps = {}; // Core employee props that are stored directly on the employee object as well as within employee attribute values.

  // @ts-expect-error TS(2339): Property 'attributesWithValues' does not exist on ... Remove this comment to see the full error message
  rawUpdatedEmployee.attributesWithValues.forEach((el) => {
    const attrId = el?.attribute?.id;
    const attrName = el?.attribute?.attributeNames[0]?.name;
    const value = el?.attributeValues[0];

    // @ts-expect-error TS(2345): Argument of type 'any' is not assignable to parame... Remove this comment to see the full error message
    employeeAttributes.push(attrId);
    employeeAttributeValues[attrId] = value;

    // WARNING: These strings are hardcoded but should be constant in the database.
    // @ts-expect-error TS(2339): Property 'firstName' does not exist on type '{}'.
    if (attrName === 'First Name') coreProps.firstName = value;
    // @ts-expect-error TS(2339): Property 'lastName' does not exist on type '{}'.
    if (attrName === 'Last Name') coreProps.lastName = value;
    // @ts-expect-error TS(2339): Property 'companyEmployeeId' does not exist on typ... Remove this comment to see the full error message
    if (attrName === 'Employee Id') coreProps.companyEmployeeId = value;
    // @ts-expect-error TS(2339): Property 'email' does not exist on type '{}'.
    if (attrName === 'email') coreProps.email = value;
  });

  const fullName =
    // @ts-expect-error TS(2339): Property 'firstName' does not exist on type '{}'.
    coreProps.firstName || coreProps.lastName
      ? // @ts-expect-error TS(2339): Property 'firstName' does not exist on type '{}'.
        `${coreProps.firstName} ${coreProps.lastName}`
      : // @ts-expect-error TS(2339): Property 'email' does not exist on type '{}'.
        coreProps.email?.substring(0, coreProps.email?.lastIndexOf('@')) || coreProps.companyEmployeeId;

  const employee = {
    // @ts-expect-error TS(2339): Property 'id' does not exist on type '{}'.
    id: Number(rawUpdatedEmployee.id),
    // @ts-expect-error TS(2339): Property 'lang' does not exist on type '{}'.
    lang: rawUpdatedEmployee.lang, // WARNING: requires mapping languageId to iso code before calling this function
    // @ts-expect-error TS(2339): Property 'enabled' does not exist on type '{}'.
    isActive: Boolean(rawUpdatedEmployee.enabled),
    ...coreProps,
    name: fullName,
    employeeAttributes,
    employeeAttributeValues,
  };

  return employee;
};

export { employeeFrom, employeesFrom, updatedEmployeeFrom };
